import { useCallback, useEffect, useState } from 'react';
import {
  safeGetPersistedValue,
  safePersistValue,
} from '../../../hooks/useLocalStorageState';
import { Folder } from '../models';

const FAVORITE_FOLDERS_STORAGE_KEY = 'ai-templates.favorite-folder-paths';
const favoriteFolderSubscribers = new Set<(folderPaths: string[]) => void>();

const getFavoriteFolderPaths = () =>
  safeGetPersistedValue<string[]>(FAVORITE_FOLDERS_STORAGE_KEY) ?? [];

const setFavoriteFolderPaths = (folderPaths: string[]) => {
  safePersistValue(FAVORITE_FOLDERS_STORAGE_KEY, folderPaths);
  favoriteFolderSubscribers.forEach(subscriber => subscriber(folderPaths));
};

export const useFavoriteTemplatingFolders = () => {
  const [favoriteFolderPaths, setFavoriteFolderPathsState] = useState<string[]>(
    () => getFavoriteFolderPaths(),
  );

  useEffect(() => {
    const subscriber = (folderPaths: string[]) => {
      setFavoriteFolderPathsState(folderPaths);
    };
    favoriteFolderSubscribers.add(subscriber);
    return () => {
      favoriteFolderSubscribers.delete(subscriber);
    };
  }, []);

  const isFavoriteFolder = useCallback(
    (folder: Pick<Folder, 'path'>) => {
      return favoriteFolderPaths.includes(folder.path);
    },
    [favoriteFolderPaths],
  );

  const toggleFavoriteFolder = useCallback((folder: Pick<Folder, 'path'>) => {
    const currentFolderPaths = getFavoriteFolderPaths();
    if (currentFolderPaths.includes(folder.path)) {
      setFavoriteFolderPaths(
        currentFolderPaths.filter(folderPath => folderPath !== folder.path),
      );
      return;
    }
    setFavoriteFolderPaths([...currentFolderPaths, folder.path]);
  }, []);

  return {
    favoriteFolderPaths,
    isFavoriteFolder,
    toggleFavoriteFolder,
  };
};
